import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Logo from "../Logo";
import api from "../../api/client";

const SIDEBAR_BG   = "#3730a3";   // indigo-800
const SIDEBAR_DARK = "#312e81";   // indigo-900 — bordes y footer
const SIDEBAR_MID  = "#4338ca";   // indigo-700 — hover
const BRAND_ORANGE = "#f97316";
const BRAND_DARK   = "#0A1128";

const MENU_ITEMS = [
  { path: "/dashboard",     label: "Dashboard",     icon: "📊", key: "dashboard" },
  { path: "/categorias",    label: "Categorías",    icon: "🏷️", key: "categorias" },
  { path: "/productos",     label: "Productos",     icon: "🛍️", key: "productos" },
  { path: "/pedidos",       label: "Pedidos",       icon: "📦", key: "pedidos" },
  { path: "/configuracion", label: "Configuración", icon: "⚙️", key: "configuracion" },
  { path: "/clientes",      label: "Clientes",      icon: "🙋", key: "clientes" },
  { path: "/usuarios",      label: "Usuarios",      icon: "👥", key: "usuarios" },
  { path: "/roles",         label: "Roles",         icon: "🔐", key: "roles" },
  { path: "/suscripcion",    label: "Suscripción",    icon: "💎", key: "suscripcion" },
  { path: "/historial-pagos", label: "Mis Pagos",    icon: "💳", key: "historial-pagos", ownerOnly: true },
];

const PLAN_COLORS = {
  gratis:      { bg: "rgba(243,244,246,0.15)", text: "rgba(255,255,255,0.6)", label: "Gratis" },
  emprendedor: { bg: "rgba(99,102,241,0.3)",   text: "#a5b4fc",               label: "Emprendedor" },
  empresa:     { bg: "rgba(139,92,246,0.3)",   text: "#c4b5fd",               label: "Empresa" },
  basico:      { bg: "rgba(99,102,241,0.3)",   text: "#a5b4fc",               label: "Emprendedor" },
  pro:         { bg: "rgba(139,92,246,0.3)",   text: "#c4b5fd",               label: "Empresa" },
};

export default function Sidebar({ open, onClose }) {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [nombreComercio, setNombreComercio] = useState(null);
  const [editando, setEditando] = useState(false);
  const [inputNombre, setInputNombre] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);

  const permisos = usuario?.permisos || {};

  useEffect(() => {
    api.get("/config/").then((r) => {
      const name = r.data.nombre_comercio || "";
      setNombreComercio(name);
      setInputNombre(name);
    }).catch(() => {});

    api.get("/suscripcion/").then((r) => {
      setPlanInfo(r.data);
    }).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate("/login"); };
  const iniciarEdicion = () => { setInputNombre(nombreComercio || ""); setEditando(true); };
  const cancelarEdicion = () => { setInputNombre(nombreComercio || ""); setEditando(false); };

  const guardarNombre = async () => {
    if (!inputNombre.trim()) return;
    setGuardando(true);
    try {
      await api.put("/config/", { nombre_comercio: inputNombre.trim() });
      setNombreComercio(inputNombre.trim());
      setEditando(false);
    } catch {
    } finally {
      setGuardando(false);
    }
  };

  const visibleItems = MENU_ITEMS.filter((item) => {
    if (item.ownerOnly && !usuario?.es_owner) return false;
    if (!permisos || Object.keys(permisos).length === 0) return true;
    return permisos[item.key] !== false;
  });

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden" onClick={onClose} />
      )}
      <aside
        style={{ background: SIDEBAR_BG, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
        className={`fixed top-0 left-0 h-full w-64 z-30 transform transition-transform duration-300
          ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 lg:static lg:z-auto`}
      >
        <div className="flex flex-col h-full">

          {/* Logo + nombre tienda */}
          <div className="px-4 pt-5 pb-4" style={{ borderBottom: `1px solid ${SIDEBAR_DARK}` }}>
            <div className="mb-4">
              <Logo width={155} dark={true} />
            </div>

            {editando ? (
              <div className="space-y-1.5">
                <input
                  value={inputNombre}
                  onChange={(e) => setInputNombre(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") guardarNombre();
                    if (e.key === "Escape") cancelarEdicion();
                  }}
                  autoFocus
                  style={{ background: SIDEBAR_DARK, borderColor: "rgba(255,255,255,0.2)" }}
                  className="w-full text-white text-sm px-2 py-1.5 rounded-md border focus:outline-none placeholder-gray-500"
                  placeholder="Nombre de tu empresa"
                />
                <div className="flex gap-1.5">
                  <button
                    onClick={guardarNombre}
                    disabled={guardando || !inputNombre.trim()}
                    style={{ background: BRAND_ORANGE, color: "#fff" }}
                    className="flex-1 text-xs py-1 rounded font-semibold disabled:opacity-50"
                  >
                    {guardando ? "..." : "Guardar"}
                  </button>
                  <button
                    onClick={cancelarEdicion}
                    style={{ borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.6)" }}
                    className="flex-1 text-xs py-1 rounded border hover:text-white transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between group">
                <div className="min-w-0">
                  <p className="text-white font-bold text-sm leading-tight truncate">
                    {nombreComercio || usuario?.nombre || "Mi Tienda"}
                  </p>
                  <p className="text-xs truncate mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                    {usuario?.email || ""}
                  </p>
                </div>
                <button
                  onClick={iniciarEdicion}
                  title="Editar nombre"
                  className="ml-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: BRAND_ORANGE }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H9v-1.414a2 2 0 01.586-1.414z" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {visibleItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
                style={({ isActive }) =>
                  isActive
                    ? { background: "rgba(249,115,22,0.2)", color: "#ffffff", borderLeft: `3px solid ${BRAND_ORANGE}`, paddingLeft: "9px" }
                    : { color: "rgba(255,255,255,0.7)", borderLeft: "3px solid transparent", paddingLeft: "9px" }
                }
                onMouseEnter={(e) => {
                  if (!e.currentTarget.style.background.includes("249,115,22")) {
                    e.currentTarget.style.background = SIDEBAR_MID;
                    e.currentTarget.style.color = "#ffffff";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.style.background.includes("249,115,22")) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "rgba(255,255,255,0.7)";
                  }
                }}
              >
                <span className="text-base w-5 text-center">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>


          {/* Footer */}
          <div className="px-3 py-4 space-y-2" style={{ borderTop: `1px solid ${SIDEBAR_DARK}` }}>
            {planInfo && (() => {
              const pc = PLAN_COLORS[planInfo.plan] || PLAN_COLORS.gratis;
              const enLimite =
                (planInfo.productos?.limite && planInfo.productos.usado >= planInfo.productos.limite) ||
                (planInfo.categorias?.limite && planInfo.categorias.usado >= planInfo.categorias.limite) ||
                (planInfo.usuarios?.limite && planInfo.usuarios.usado >= planInfo.usuarios.limite);
              return (
                <NavLink
                  to="/suscripcion"
                  onClick={onClose}
                  className="flex items-center justify-between px-3 py-2 rounded-lg transition-all"
                  style={{ background: pc.bg }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: pc.text }}>Plan</span>
                    <span className="text-xs font-semibold capitalize" style={{ color: pc.text }}>{pc.label}</span>
                  </div>
                  {enLimite && (
                    <span className="text-xs font-medium text-red-400">Limite</span>
                  )}
                </NavLink>
              );
            })()}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
              style={{ color: "rgba(255,255,255,0.5)", borderLeft: "3px solid transparent", paddingLeft: "9px" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.12)"; e.currentTarget.style.color = "#fca5a5"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
            >
              <span className="w-5 text-center">🚪</span>
              Cerrar sesión
            </button>
          </div>

        </div>
      </aside>
    </>
  );
}
